import { browser } from '$app/environment';
import { AppMode, appMode, cfgOptimoveEnabled } from '@infinitro/config';
export const OPTIMOVE_EVENTS = {
    REGISTRATION: 'registration',
    LOGIN: 'login'
};
const PENDING_AUTH_EVENT_KEY = 'optimove-pending-auth-event';
const PENDING_PAGE_VISIT_KEY = 'optimove-pending-page-visit';
const REPORT_EVENT_TIMEOUT_MS = 1500;
const SDK_FLUSH_POLL_MS = 100;
const SDK_FLUSH_MAX_WAIT_MS = 15000;
let sdkGaveUp = false;
let authEventFlushInFlight = false;
let pendingAuthFlushWaitingLogged = false;
const logOptimove = (message, details) => {
    const suffix = details ? ` ${JSON.stringify(details)}` : '';
    console.log(`optimove ${message}${suffix}`);
};
const hasPendingAuthEvent = () => {
    if (!browser)
        return false;
    try {
        return !!sessionStorage.getItem(PENDING_AUTH_EVENT_KEY);
    }
    catch {
        return false;
    }
};
const logPendingAuthFlushBlocked = (reason) => {
    if (!hasPendingAuthEvent() || pendingAuthFlushWaitingLogged)
        return;
    pendingAuthFlushWaitingLogged = true;
    logOptimove(`pending auth flush waiting: ${reason}`);
};
const clearPendingAuthFlushWaitingLog = () => {
    pendingAuthFlushWaitingLogged = false;
};
const canFlushPendingAuthEvent = () => {
    // Auth is queued on the public site, then the user is redirected to the player app.
    // Wait until that redirect completes before reporting — sessionStorage survives the
    // same-origin navigation, but flushing on public would delete the event too early.
    return appMode !== AppMode.Public;
};
const getOptimoveApi = () => {
    if (!browser || !cfgOptimoveEnabled())
        return undefined;
    const sdk = globalThis.optimoveSDK;
    if (!sdk || typeof sdk !== 'object' || !sdk.API)
        return undefined;
    return sdk.API;
};
const buildEventContext = (extra) => {
    const context = {};
    if (!extra)
        return context;
    for (const [key, value] of Object.entries(extra)) {
        if (value !== undefined) {
            context[key] = value;
        }
    }
    return context;
};
const abandonPendingOptimoveEvents = () => {
    if (!browser)
        return;
    sdkGaveUp = true;
    try {
        sessionStorage.removeItem(PENDING_PAGE_VISIT_KEY);
        // Keep pending auth events — the SDK may still load after this timeout.
    }
    catch {
        // ignore
    }
    clearPendingAuthFlushWaitingLog();
    logOptimove(`SDK did not load within ${SDK_FLUSH_MAX_WAIT_MS}ms, dropped pending page visits`);
};
const reportOptimoveEvent = (eventName, playerId, parameters) => {
    return new Promise((resolve) => {
        const api = getOptimoveApi();
        if (!api) {
            logOptimove(`skipped ${eventName} report, SDK not available`, { playerId });
            resolve(false);
            return;
        }
        logOptimove(`reporting ${eventName}`, { playerId, parameters });
        let settled = false;
        const finish = (success) => {
            if (settled)
                return;
            settled = true;
            if (success) {
                logOptimove(`${eventName} reported successfully`, { playerId });
            }
            else {
                logOptimove(`${eventName} report not confirmed`, { playerId });
            }
            resolve(success);
        };
        const timeout = setTimeout(() => {
            logOptimove(`${eventName} report timed out after ${REPORT_EVENT_TIMEOUT_MS}ms`, { playerId });
            finish(false);
        }, REPORT_EVENT_TIMEOUT_MS);
        try {
            api.setUserId(playerId);
            api.reportEvent(eventName, parameters, () => {
                clearTimeout(timeout);
                finish(true);
            }, playerId);
        }
        catch (error) {
            clearTimeout(timeout);
            logOptimove(`${eventName} report failed: ${error}`, { playerId });
            finish(false);
        }
    });
};
export const flushPendingOptimovePageVisit = async () => {
    if (!browser || !cfgOptimoveEnabled() || sdkGaveUp || !getOptimoveApi())
        return;
    let raw;
    try {
        raw = sessionStorage.getItem(PENDING_PAGE_VISIT_KEY);
        if (!raw)
            return;
    }
    catch {
        return;
    }
    try {
        const data = JSON.parse(raw);
        if (!data.url)
            return;
        const api = getOptimoveApi();
        if (!api)
            return;
        api.setPageVisit(data.url, data.title, data.category);
        sessionStorage.removeItem(PENDING_PAGE_VISIT_KEY);
    }
    catch (error) {
        logOptimove(`pending page visit failed: ${error}`);
    }
};
export const flushPendingOptimoveAuthEvent = async () => {
    if (!browser) {
        logPendingAuthFlushBlocked('not in browser');
        return undefined;
    }
    if (!cfgOptimoveEnabled()) {
        logPendingAuthFlushBlocked('optimove disabled');
        return undefined;
    }
    if (!canFlushPendingAuthEvent()) {
        logPendingAuthFlushBlocked('waiting for post-auth redirect');
        return undefined;
    }
    if (!getOptimoveApi()) {
        logPendingAuthFlushBlocked(sdkGaveUp ? 'SDK gave up loading' : 'SDK not ready');
        return undefined;
    }
    if (authEventFlushInFlight)
        return undefined;
    let raw;
    try {
        raw = sessionStorage.getItem(PENDING_AUTH_EVENT_KEY);
        if (!raw)
            return undefined;
    }
    catch (error) {
        logOptimove(`failed to read pending auth event from sessionStorage: ${error}`);
        return undefined;
    }
    authEventFlushInFlight = true;
    try {
        const data = JSON.parse(raw);
        if (!data.playerId || !data.event) {
            logOptimove('skipped pending auth flush, invalid queued payload', {
                hasPlayerId: !!data.playerId,
                event: data.event
            });
            return undefined;
        }
        clearPendingAuthFlushWaitingLog();
        logOptimove('flushing pending auth event', {
            event: data.event,
            playerId: data.playerId,
            authMethod: data.authMethod,
            visitId: data.visitId
        });
        const context = buildEventContext({
            visit_id: data.visitId,
            auth_method: data.authMethod,
            ...(data.event === 'registration' && data.email ? { email: data.email } : {})
        });
        const eventName = data.event === 'login' ? OPTIMOVE_EVENTS.LOGIN : OPTIMOVE_EVENTS.REGISTRATION;
        const reported = await reportOptimoveEvent(eventName, data.playerId, context);
        if (!reported)
            return undefined;
        sessionStorage.removeItem(PENDING_AUTH_EVENT_KEY);
        logOptimove('pending auth event flushed', { event: data.event, playerId: data.playerId });
        return data.playerId;
    }
    catch (error) {
        logOptimove(`pending auth event failed: ${error}`);
        return undefined;
    }
    finally {
        authEventFlushInFlight = false;
    }
};
export const flushPendingOptimoveEvents = async () => {
    await flushPendingOptimovePageVisit();
    return flushPendingOptimoveAuthEvent();
};
export const startOptimoveSdkFlushWatcher = () => {
    if (!browser || !cfgOptimoveEnabled())
        return () => { };
    logOptimove('started SDK flush watcher', {
        pollMs: SDK_FLUSH_POLL_MS,
        maxWaitMs: SDK_FLUSH_MAX_WAIT_MS,
        hasPendingAuth: hasPendingAuthEvent()
    });
    const tryFlush = () => {
        void flushPendingOptimoveEvents();
    };
    tryFlush();
    const interval = setInterval(tryFlush, SDK_FLUSH_POLL_MS);
    const timeout = setTimeout(() => {
        clearInterval(interval);
        if (!getOptimoveApi()) {
            logOptimove('SDK flush watcher timed out', { hasPendingAuth: hasPendingAuthEvent() });
            abandonPendingOptimoveEvents();
        }
    }, SDK_FLUSH_MAX_WAIT_MS);
    return () => {
        clearInterval(interval);
        clearTimeout(timeout);
    };
};
export const setOptimoveUserId = (playerId) => {
    if (!browser || !cfgOptimoveEnabled() || !playerId)
        return;
    const api = getOptimoveApi();
    if (!api)
        return;
    try {
        api.setUserId(playerId);
    }
    catch (error) {
        logOptimove(`setUserId failed: ${error}`, { playerId });
    }
};
export const trackOptimovePageVisit = (url, title, category) => {
    if (!browser || !cfgOptimoveEnabled())
        return;
    const api = getOptimoveApi();
    if (api) {
        try {
            api.setPageVisit(url, title, category);
        }
        catch (error) {
            logOptimove(`setPageVisit failed: ${error}`);
        }
        return;
    }
    if (sdkGaveUp)
        return;
    try {
        sessionStorage.setItem(PENDING_PAGE_VISIT_KEY, JSON.stringify({ url, title, category }));
    }
    catch (error) {
        logOptimove(`failed to queue page visit: ${error}`);
        return;
    }
    void flushPendingOptimovePageVisit();
};
export const queueOptimoveAuthEvent = (event) => {
    if (!browser) {
        logOptimove('skipped queue auth event, not in browser', { event: event.event });
        return;
    }
    if (!cfgOptimoveEnabled()) {
        logOptimove('skipped queue auth event, optimove disabled', { event: event.event });
        return;
    }
    if (!event.playerId) {
        logOptimove('skipped queue auth event, missing playerId', { event: event.event });
        return;
    }
    try {
        sessionStorage.setItem(PENDING_AUTH_EVENT_KEY, JSON.stringify(event));
        clearPendingAuthFlushWaitingLog();
        logOptimove('queued auth event', {
            event: event.event,
            playerId: event.playerId,
            authMethod: event.authMethod,
            visitId: event.visitId,
            sdkReady: !!getOptimoveApi()
        });
    }
    catch (error) {
        logOptimove(`failed to queue auth event: ${error}`, {
            event: event.event,
            playerId: event.playerId
        });
    }
};
export const trackOptimoveLogin = async (playerId, context) => {
    if (!playerId) {
        logOptimove('skipped login report, missing playerId');
        return;
    }
    if (!getOptimoveApi()) {
        logOptimove('skipped login report, SDK not available', { playerId });
        return;
    }
    await reportOptimoveEvent(OPTIMOVE_EVENTS.LOGIN, playerId, buildEventContext(context));
};
export const trackOptimoveRegistration = async (playerId, email, context) => {
    if (!playerId) {
        logOptimove('skipped registration report, missing playerId');
        return;
    }
    if (!getOptimoveApi()) {
        logOptimove('skipped registration report, SDK not available', { playerId });
        return;
    }
    await reportOptimoveEvent(OPTIMOVE_EVENTS.REGISTRATION, playerId, buildEventContext({ ...context, ...(email ? { email } : {}) }));
};
